/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import axiosInstance from '../functions/AxiosInstance'; // Asegúrate de que la ruta sea correcta

// Definir el tipo de los datos para el servicio
interface ServiceData {
  name: string;
  description: string;
  price: number;
  serviceType: 'installation' | 'customization' | 'support' | 'maintenance' | 'other';
}

// Función para crear un servicio
const createService = async (serviceData: ServiceData) => {
  const response = await axiosInstance.post('/services/', serviceData);
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para crear un servicio
export const useCreateService = () => {
  const mutation = useMutation({
    mutationFn: createService, // Función para crear el servicio
    onSuccess: () => {
      toast.success('El servicio ha sido creado con éxito.');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      const errorMessage = error?.response?.data?.message || 'Error desconocido';
      toast.error(`Error al crear el servicio: ${errorMessage}`);
      console.error('Error al crear el servicio:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga
    isSuccess: mutation.isSuccess, // Indica si la creación fue exitosa
  };
};


// Función para obtener los datos de los servicios
const fetchServices = async () => {
    const response = await axiosInstance.get('/services/');
    return response.data.services; // Devuelve los datos de la respuesta
  };
  
  // Hook para listar los servicios
  export const useGetServices = () => {
    const { data, error, isLoading, isError } = useQuery({
      queryKey: ['my-services'], // Esto es lo que identifica la consulta en React Query
      queryFn: async () => {
        try {
          return await fetchServices();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          console.error('Error al obtener los servicios:', error);
          throw error;
        }
      },
      refetchOnWindowFocus: false, // Opcional: Evita que los datos se vuelvan a obtener cuando el foco de la ventana cambia
      refetchInterval: 60000, // Refetch cada 1 minuto (60,000 ms)
    });
  
    return {
      services: data,    // Servicios obtenidos
      isLoading,         // Si está cargando
      isError,           // Si hubo un error
      error,             // El error completo (si existe)
    };
  };
  

  // Función para obtener los detalles de un servicio por ID
const fetchServiceDetails = async (serviceId: string) => {
    const response = await axiosInstance.get(`/services/${serviceId}`);
    return response.data; // Devuelve los datos de la respuesta
  };
  
  // Hook para obtener los detalles de un servicio específico
  export const useGetServiceDetails = (serviceId: string) => {
    const { data, error, isLoading, isError } = useQuery({
      queryKey: ['service-details', serviceId], // Usa el ID del servicio como parte de la clave
      queryFn: async () => {
        try {
          return await fetchServiceDetails(serviceId);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          console.error('Error al obtener los detalles del servicio:', error);
          throw error;
        }
      },
      refetchOnWindowFocus: false, // Opcional: Evita que los datos se vuelvan a obtener cuando el foco de la ventana cambia
    });
  
    return {
      service: data,      // Detalles del servicio obtenido
      isLoading,          // Si está cargando
      isError,            // Si hubo un error
      error,              // El error completo (si existe)
    };
  };
  

  // Función para actualizar un servicio por su ID
const updateService = async ({ serviceId, serviceData }: { serviceId: string, serviceData: any }) => {
    const response = await axiosInstance.put(`/services/${serviceId}`, serviceData);
    return response.data; // Devuelve los datos de la respuesta
  };
  
  // Hook para actualizar un servicio
  export const useUpdateService = () => {
    const mutation = useMutation({
      mutationFn: updateService, // Función para actualizar el servicio
      onSuccess: () => {
        toast.success('Servicio actualizado con éxito.');
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || 'Error desconocido';
        toast.error(`Error al actualizar el servicio: ${errorMessage}`);
        console.error('Error al actualizar el servicio:', errorMessage);
      },
    });
  
    return {
      ...mutation,
      isLoading: mutation.isPending, // Estado de carga de la mutación
      isSuccess: mutation.isSuccess, // Indica si la actualización fue exitosa
    };
  };
  
// Función para eliminar un servicio por su ID
const deleteService = async (serviceId: string) => {
    try {
      const response = await axiosInstance.delete(`/services/${serviceId}`);
      return response.data; // Devuelve los datos de la respuesta
    } catch (error) {
      // Error handling específico
      const errorMessage = error?.response?.data?.message || 'Error desconocido';
      throw new Error(errorMessage); // Lanza el error para manejarlo en el hook
    }
  };
  
  // Hook para eliminar un servicio
  export const useDeleteService = () => {
    const mutation = useMutation({
      mutationFn: deleteService, // Función para eliminar el servicio
      onMutate: () => {
        // Estado antes de que la mutación empiece, puedes usarlo para mostrar un spinner o algún indicativo de carga
        toast.success('Eliminando el servicio...');
      },
      onSuccess: () => {
        toast.success('Servicio eliminado con éxito.');
      },
      onError: (error: any) => {
        const errorMessage = error?.message || 'Error desconocido';
        toast.error(`Error al eliminar el servicio: ${errorMessage}`);
        console.error('Error al eliminar el servicio:', errorMessage);
      },
      onSettled: () => {
        // Opcional: Puede ser útil para realizar tareas adicionales después de que la mutación termine, como refetching datos
        console.log('Eliminación de servicio completada.');
      },
    });
  
    return {
      ...mutation,
      isLoading: mutation.isPending, // Estado de carga de la mutación
      isSuccess: mutation.isSuccess, // Indica si la eliminación fue exitosa
      isError: mutation.isError, // Si ocurrió un error
      error: mutation.error, // El error completo (si existe)
    };
  };
  